import React, { useState, useMemo, useEffect, useRef } from 'react';
import { SKU } from 'src/redux/billing/billing.types';
import { Check, ChevronDown, Lightbulb, Receipt } from 'lucide-react';
import { fuzzyFilterSimilarity } from 'src/services/fuzzy-match';
import { useUpdateCampaignMutation } from '../endpoints/accounts.endpoints';
import { Campaign } from '../types/client-management.types';
import { sortAsc } from '../utils';

interface SkuSelectorProps {
  skus: Array<SKU>;
  campaign: Campaign;
  onSkuSelect?: (skuId: string) => void;
  className?: string;
//   isLoading?: boolean;
}

const SkuSelector: React.FC<SkuSelectorProps> = ({
  campaign,
  skus,
  onSkuSelect,
  className,
//   isLoading = false,
}) => {
  const [selectedSku, setSelectedSku] = useState<string>(campaign?.sku || '');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filteredSkus, setFilteredSkus] = useState<SKU[]>(skus);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [updateCampaign, { isLoading }] = useUpdateCampaignMutation();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDropdown]);

  // Smart suggestion logic based on name matching
  const suggestedSkus = useMemo(() => {
    if (skus.length === 0) return [];

    const results = fuzzyFilterSimilarity(skus, campaign.title, (sku: SKU) => sku.name, { limit: 3, threshold: 15 });
    return results.map((result) => result.item).slice(0, 1);
  }, [skus, campaign]);

  const handleSkuSelect = (selectedSkuId: string) => {
    setSelectedSku(selectedSkuId);
    setShowDropdown(false);
    if (onSkuSelect) {
      onSkuSelect(selectedSkuId);
    }
  };

  const handleDropdownToggle = () => {
    setShowDropdown(!showDropdown);
  };

  const handleSearch = (searchQuery: string) => {
    setSearchQuery(searchQuery);
    setFilteredSkus(skus.filter(sku => sku.name && sku.name.toLowerCase().includes(searchQuery && searchQuery.toLowerCase())));
  };

  return (
    <div className="relative max-w-full">
      <div
        onClick={!isLoading ? handleDropdownToggle : undefined}
        className={`text-xs text-gray-500 flex flex-row mt-2 w-fit max-w-full px-2 py-1 rounded-full items-center ${
          isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200 cursor-pointer'
        }`}
      >
        <Receipt className={`size-4 ${isLoading ? '' : 'cursor-pointer'}`} />
        <div
          className={`text-sm font-normal text-gray-800 ml-2 items-center truncate max-w-32 ${
            isLoading ? '' : 'cursor-pointer'
          }`}
          title={campaign.sku ? skus.find(sku => sku.id === campaign.sku)?.name : 'Unassigned'}
        >
          {isLoading ? 'Updating...' : campaign.sku ? skus.find(sku => sku.id === campaign.sku)?.name : 'Unassigned'}
        </div>
        <ChevronDown className={`size-4 ml-2 ${isLoading ? '' : 'cursor-pointer'}`} />
      </div>
      {!campaign.sku && suggestedSkus && suggestedSkus.length > 0 && (
        <>
          <div
            onClick={() => handleSkuSelect(suggestedSkus[0].id)}
            className='flex w-full text-sm text-gray-500 ml-2 cursor-pointer underline items-center'
          >
            <Lightbulb className='size-3 mr-1 bg-yellow-500 rounded-full p-0.5 text-white' />
            {suggestedSkus[0].name}
          </div>
        </>
      )}

      {campaign.attention_needed && campaign.sku && (
        <div
          onClick={() => handleSkuSelect(campaign.sku)}
          className='flex w-full text-sm text-gray-500 ml-2 cursor-pointer underline items-center'
        >
          <Check className='size-3 mr-1 bg-green-501 rounded-full p-0.5 text-white' />
          Verify
        </div>
      )}
      {/* Dropdown menu */}
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto w-fit"
        >
          <div className="p-1">
            <div className="px-3 py-2 text-xs text-gray-500 font-medium border-b border-gray-100">
              <input
                type="text"
                placeholder="Search SKUs"
                className="w-full text-sm text-gray-500 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
            {skus.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500 text-center">
                No SKUs available
              </div>
            ) : (
              sortAsc(filteredSkus, 'name').map((sku) => (
                <button
                  key={sku.id}
                  onClick={() => handleSkuSelect(sku.id)}
                  className={`w-full text-left px-3 py-2 hover:bg-gray-100 rounded text-sm transition-colors duration-150 flex items-center gap-2 ${
                    selectedSku === sku.id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                  }`}
                >
                  <Receipt className="size-3 flex-shrink-0" />
                  <span className="truncate">
                    {sku.name}
                  </span>
                  {selectedSku === sku.id && (
                    <div className="ml-auto">
                      <div className="w-2 h-2 bg-blue-500 rounded-full" />
                    </div>
                  )}
                </button>
              ))
            )}
            {campaign.sku && campaign.sku && (
              <>
                <div className="border-t border-gray-100 mt-1 pt-1">
                  <button
                    onClick={() => handleSkuSelect('')}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded text-sm transition-colors duration-150 text-gray-500 flex items-center gap-2"
                  >
                    <div className="size-3 flex-shrink-0 border border-gray-300 rounded" />
                    <span>
                      Unassign Sku
                    </span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SkuSelector;
